// Screen: "Seasons"
import React, { useState, useEffect } from 'react';
import { getSeasons, getCurrentSeason, getSeasonStandings } from '../apiClient';
import SEO from '../components/SEO';

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

const METRIC_LABELS = {
  top_earners: 'Top Earners', win_rate: 'Win Rate', games_played: 'Most Games',
  call_success: 'Best Callers', point_differential: 'Low Scores', longest_streak: 'Win Streaks'
};

export default function SeasonsPage() {
  const isMobile = useIsMobile();
  const [seasons, setSeasons] = useState([]);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('top_earners');
  const [standings, setStandings] = useState([]);

  useEffect(() => {
    getSeasons().then(data => setSeasons(data.seasons || []));
    getCurrentSeason().then(data => {
      if (data.season) {
        setCurrentSeason(data.season);
        setSelectedSeason(data.season.season_id);
      }
    });
  }, []);

  useEffect(() => {
    if (selectedSeason) {
      getSeasonStandings(selectedSeason, selectedMetric).then(data => {
        setStandings(data.standings || []);
      });
    }
  }, [selectedSeason, selectedMetric]);

  return (
    <>
    <SEO
      title="Seasonal Competitions"
      description="Track seasonal Buck Euchre competitions and standings. Compete each season for top rankings and rewards."
      path="/seasons"
    />
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '40px 24px',
      maxWidth: 700, margin: '0 auto',
      fontFamily: "'Georgia', serif", color: '#e2e8f0'
    }}>
      <h1 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, marginBottom: isMobile ? 16 : 24 }}>Seasons</h1>

      {/* Current Season Banner with Countdown */}
      {currentSeason && (() => {
        const endDate = new Date(currentSeason.end_date);
        const now = new Date();
        const daysLeft = Math.max(0, Math.ceil((endDate - now) / 86400000));
        const isEndingSoon = daysLeft <= 5;
        return (
          <div style={{
            background: isEndingSoon
              ? 'linear-gradient(135deg, #3d1f1f, #0d1117)'
              : 'linear-gradient(135deg, #1c3d2a, #0d1117)',
            border: `1px solid ${isEndingSoon ? '#f85149' : '#3fb950'}`, borderRadius: 12,
            padding: isMobile ? 16 : 24, marginBottom: 20
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 10, color: isEndingSoon ? '#f85149' : '#3fb950', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>
                  {isEndingSoon ? 'Season Ending Soon!' : 'Active Season'}
                </div>
                <div style={{ fontSize: isMobile ? 18 : 24, fontWeight: 700, color: '#e2e8f0' }}>
                  {currentSeason.name}
                </div>
                {currentSeason.theme && (
                  <div style={{ fontSize: 12, color: '#8b949e', marginTop: 4 }}>{currentSeason.theme}</div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                {daysLeft > 0 ? (
                  <div style={{
                    fontSize: isMobile ? 20 : 28, fontWeight: 700,
                    color: isEndingSoon ? '#f85149' : '#fbbf24'
                  }}>
                    {daysLeft}d
                    <div style={{ fontSize: 10, color: '#8b949e', fontWeight: 400 }}>remaining</div>
                  </div>
                ) : (
                  <div style={{ fontSize: 14, color: '#f85149', fontWeight: 600 }}>Season Over</div>
                )}
              </div>
            </div>

            {/* Season Reward Tiers */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr',
              gap: 8, marginTop: 8
            }}>
              {[
                { label: 'Top 3', reward: '50 Bucks + Badge', color: '#d4af37' },
                { label: 'Top 10', reward: '50 Bucks + Badge', color: '#c0c0c0' },
                { label: 'Top 25', reward: '25 Bucks + Badge', color: '#cd7f32' },
                { label: 'Top 50%', reward: 'Participation Badge', color: '#8b949e' },
              ].map(tier => (
                <div key={tier.label} style={{
                  background: '#0d111780', borderRadius: 6, padding: '6px 10px',
                  border: `1px solid ${tier.color}30`, textAlign: 'center'
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: tier.color }}>{tier.label}</div>
                  <div style={{ fontSize: 9, color: '#8b949e', marginTop: 2 }}>{tier.reward}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Season Selector */}
      {seasons.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <select
            value={selectedSeason || ''}
            onChange={e => setSelectedSeason(e.target.value)}
            style={{
              background: '#0d1117', border: '1px solid #30363d', borderRadius: 6,
              color: '#e2e8f0', padding: '8px 12px', fontSize: 13, width: '100%',
              cursor: 'pointer', fontFamily: "'Georgia', serif"
            }}
          >
            <option value="">Select a season...</option>
            {seasons.map(s => (
              <option key={s.season_id} value={s.season_id}>
                {s.name} ({s.status})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Metric tabs */}
      {selectedSeason && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 16 }}>
          {Object.entries(METRIC_LABELS).map(([key, label]) => (
            <button key={key} onClick={() => setSelectedMetric(key)} style={{
              background: selectedMetric === key ? '#238636' : 'transparent',
              color: selectedMetric === key ? '#fff' : '#8b949e',
              border: selectedMetric === key ? 'none' : '1px solid #30363d',
              borderRadius: 6, padding: isMobile ? '5px 8px' : '7px 12px',
              fontSize: isMobile ? 10 : 12, cursor: 'pointer', fontWeight: 600
            }}>
              {isMobile ? label.split(' ')[0] : label}
            </button>
          ))}
        </div>
      )}

      {/* Standings */}
      {selectedSeason && (
        <div style={{
          background: '#161b22', border: '1px solid #30363d', borderRadius: 10,
          padding: isMobile ? 8 : 12
        }}>
          {standings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#8b949e', fontSize: 14 }}>
              No standings yet for this season.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {standings.map((s, i) => (
                <div key={s.standing_id || i} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  background: i < 3 ? 'rgba(255,255,255,0.03)' : 'transparent',
                  borderRadius: 6
                }}>
                  <span style={{
                    width: 28, textAlign: 'center', fontSize: i < 3 ? 16 : 12,
                    color: i === 0 ? '#d4af37' : i === 1 ? '#c0c0c0' : i === 2 ? '#cd7f32' : '#8b949e',
                    fontWeight: i < 3 ? 700 : 400
                  }}>
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${s.final_rank || i + 1}`}
                  </span>
                  <span style={{ flex: 1, fontSize: 14, color: '#c9d1d9', fontWeight: 500 }}>
                    {s.nickname || 'Player'}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#58a6ff' }}>
                    {typeof s.value === 'number' ? s.value.toLocaleString() : s.value}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* No seasons message */}
      {seasons.length === 0 && (
        <div style={{
          background: '#161b22', border: '1px solid #30363d', borderRadius: 10,
          padding: 40, textAlign: 'center'
        }}>
          <div style={{ color: '#8b949e', fontSize: 16, marginBottom: 8 }}>No seasons yet</div>
          <div style={{ color: '#484f58', fontSize: 13 }}>Seasons will appear here when created by an admin.</div>
        </div>
      )}
    </div>
    </>
  );
}
